import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PageHeading, SearchField, TableScroll, Toolbar, ToolbarSpacer } from '@hp/ui';
import { Navigate } from 'react-router';
import { useCan, useMe } from '../api/me';
import { useEvents } from '../features/audit/queries';
import { ErrorAlert } from '../components/error-alert';
import { RelativeTime } from '../components/relative-time';
import { LoadMore } from '../components/ui/load-more';
import { BrutTable, BrutTheadRow } from '../components/ui/table';
import { prettyJson, truncate } from '../lib/format';

function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return debounced;
}

export default function Audit() {
  const { t } = useTranslation(['audit', 'common']);
  const [actionInput, setActionInput] = useState('');
  const action = useDebounced(actionInput.trim(), 300);
  const me = useMe();
  const canRead = useCan()('audit.read');
  const events = useEvents(action, canRead);

  const items = useMemo(() => events.data?.pages.flatMap((p) => p.items) ?? [], [events.data]);

  if (me.isLoading) return null;
  if (!canRead) return <Navigate to="/crm-data" replace />;

  return (
    <div className="bo-sections">
      <PageHeading title={t('title')} description={t('description')} />

      <ErrorAlert error={events.error} />

      <section className="hp-card">
        <Toolbar className="bo-toolbar">
          <SearchField
            className="bo-search bo-search--toolbar"
            placeholder={t('filterPlaceholder')}
            value={actionInput}
            onChange={(e) => setActionInput(e.target.value)}
            aria-label={t('filterAria')}
          />
          <ToolbarSpacer />
          <span className="bo-muted bo-mono">
            {events.hasNextPage ? t('loadedMore', { count: items.length }) : t('loaded', { count: items.length })}
          </span>
        </Toolbar>
        <TableScroll label={t('tableLabel')}>
          <BrutTable className="bo-audit-table">
            <BrutTheadRow>
              <th>{t('common:table.id')}</th>
              <th>{t('columns.at')}</th>
              <th>{t('columns.action')}</th>
              <th>{t('columns.actor')}</th>
              <th>{t('columns.entity')}</th>
              <th>{t('columns.payload')}</th>
            </BrutTheadRow>
            <tbody>
              {items.map((event) => (
                <tr key={event.id}>
                  <td className="bo-cell-muted">{event.id}</td>
                  <td className="bo-nowrap">
                    <RelativeTime value={event.at} />
                  </td>
                  <td className="bo-strong bo-nowrap">{event.action}</td>
                  <td className="bo-cell-muted bo-nowrap" title={`${event.actorKind ?? ''}:${event.actorId ?? ''}`}>
                    {event.actorKind ?? '—'}
                    {event.actorId ? `:${truncate(event.actorId, 12)}` : ''}
                  </td>
                  <td className="bo-cell-muted bo-nowrap">
                    {event.entityKind ? `${event.entityKind}${event.entityId ? ` #${event.entityId}` : ''}` : '—'}
                  </td>
                  <td className="bo-cell-payload">
                    {event.payload !== null && event.payload !== undefined ? (
                      <details className="bo-payload">
                        <summary>{truncate(JSON.stringify(event.payload) ?? '', 60)}</summary>
                        <pre className="bo-code">{prettyJson(event.payload)}</pre>
                      </details>
                    ) : (
                      <span className="bo-muted">—</span>
                    )}
                  </td>
                </tr>
              ))}
              {!events.isLoading && items.length === 0 && (
                <tr>
                  <td colSpan={6} className="bo-cell-empty">
                    {t('empty')}
                  </td>
                </tr>
              )}
              {events.isLoading && (
                <tr>
                  <td colSpan={6} className="bo-cell-empty">
                    {t('common:actions.loading')}
                  </td>
                </tr>
              )}
            </tbody>
          </BrutTable>
        </TableScroll>
        {events.hasNextPage && (
          <div className="hp-card__body bo-row">
            <LoadMore
              hasNextPage={events.hasNextPage}
              isFetchingNextPage={events.isFetchingNextPage}
              onClick={() => void events.fetchNextPage()}
            />
          </div>
        )}
      </section>
    </div>
  );
}
